/**
 * Chat Admin Panel
 * Provides admin interface for monitoring user database logs
 * 
 * ADMIN ONLY - FOR USER DATABASE MONITORING
 * Access restricted to paymanshu.sharma@maersk.com
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { CitationRenderer } from '../Citation';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { chatApi, SchemaStatusResponse } from '../../services/api';
import {
  chatAdminGetAllUsers,
  chatAdminGetAuthorizedUsers,
  chatAdminGetConversations,
  chatAdminGetStats,
  chatAdminGetUserDetails,
  chatAdminExportData,
  chatAdminImportData,
  chatAdminUpdateWhitelist,
  chatAdminUpdateUser,
  chatAdminRemoveWhitelist,
  User,
  UserType,
  WhitelistEntry,
  ConversationMessage,
  ChatAdminStats,
  UserDetails,
} from '../../services/chatAdminService';

interface ChatAdminPanelProps {
  adminEmail: string;
  onClose: () => void;
}

interface UserDetailsModalProps {
  userDetails: UserDetails | null;
  onClose: () => void;
}

interface ConversationDetailsModalProps {
  conversation: ConversationMessage | null;
  onClose: () => void;
}

type TabId = 'users' | 'whitelist' | 'conversations' | 'stats' | 'schema';
type UserGroupFilter = 'all' | 'authorized' | 'revoked' | 'waitlisted_no_access' | 'logged_in_no_access';
type SortDir = 'asc' | 'desc';
interface SortConfig { key: string; dir: SortDir; }

const formatExecTime = (ms: number | null | undefined): string => {
  if (!ms || ms <= 0) return 'N/A';
  const cs = Math.round(ms / 10);
  const m = Math.floor(cs / 6000);
  const s = Math.floor((cs % 6000) / 100);
  const c = cs % 100;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}:${c.toString().padStart(2, '0')}`;
};

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).catch(() => {});
};

/**
 * Same pipeline as Message.tsx getFormattedContent(): stored responses are often
 * JSON.stringify({ summary, details }); extract markdown and unescape newlines.
 */
function formatStoredGeneratedResponseForDisplay(raw: string): string {
  let contentString = raw;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const o = parsed as Record<string, unknown>;
      const summary = typeof o.summary === 'string' ? o.summary : '';
      const details = typeof o.details === 'string' ? o.details : '';
      const citedReport = typeof o.cited_report === 'string' ? o.cited_report : '';
      const responseStr = typeof o.response === 'string' ? o.response : '';
      if (summary && details) {
        contentString = `${summary}\n\n${details}`;
      } else if (summary && citedReport) {
        contentString = `${summary}\n\n${citedReport}`;
      } else if (summary) {
        contentString = summary;
      } else if (details) {
        contentString = details;
      } else if (citedReport) {
        contentString = citedReport;
      } else if (responseStr) {
        contentString = responseStr;
      } else {
        contentString = raw;
      }
    }
  } catch {
    // plain markdown / non-JSON
  }
  return contentString
    .replace(/\\{2,}n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\r\n|\r/g, '\n');
}

// ---------------------------------------------------------------------------
// Conversation Details Modal
// ---------------------------------------------------------------------------
const ConversationDetailsModal: React.FC<ConversationDetailsModalProps> = ({ conversation, onClose }) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = (field: string, text: string) => {
    copyToClipboard(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  if (!conversation) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">Conversation Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-700 p-4 rounded">
                <div className="text-gray-400 text-sm">Message ID</div>
                <div className="text-white font-mono text-sm">{conversation.message_id}</div>
              </div>
              <div className="bg-gray-700 p-4 rounded">
                <div className="text-gray-400 text-sm">Conversation ID</div>
                <div className="text-white font-mono text-sm">{conversation.conversation_id}</div>
              </div>
              <div className="bg-gray-700 p-4 rounded">
                <div className="text-gray-400 text-sm">Job ID</div>
                <div className="text-white font-mono text-sm break-all">{conversation.job_id}</div>
              </div>
              <div className="bg-gray-700 p-4 rounded">
                <div className="text-gray-400 text-sm">User Email</div>
                <div className="text-white text-sm">{conversation.email}</div>
              </div>
            </div>
          </div>

          {/* Query Text */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Query Text</h3>
            <div className="bg-gray-700 p-4 rounded">
              <div className="text-white whitespace-pre-wrap break-words">{conversation.user_query_text}</div>
            </div>
          </div>

          {/* Status Information */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Status Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-gray-700 p-4 rounded">
                <div className="text-gray-400 text-sm mb-2">Response Status</div>
                <span className={`px-3 py-1 rounded text-sm font-semibold ${
                  conversation.response_status === 'completed' ? 'bg-green-600 text-white' :
                  conversation.response_status === 'failed' ? 'bg-red-600 text-white' :
                  conversation.response_status === 'in_progress' ? 'bg-blue-600 text-white' :
                  'bg-gray-600 text-white'
                }`}>{conversation.response_status || 'Unknown'}</span>
              </div>
              <div className="bg-gray-700 p-4 rounded">
                <div className="text-gray-400 text-sm mb-2">Citation Status</div>
                <span className={`px-3 py-1 rounded text-sm font-semibold ${
                  conversation.citation_status === 'completed' || conversation.citation_status === 'generated' ? 'bg-green-600 text-white' :
                  conversation.citation_status === 'failed' ? 'bg-red-600 text-white' :
                  conversation.citation_status === 'in_progress' ? 'bg-blue-600 text-white' :
                  'bg-gray-600 text-white'
                }`}>{conversation.citation_status || 'Unknown'}</span>
              </div>
              <div className="bg-gray-700 p-4 rounded">
                <div className="text-gray-400 text-sm">Execution Time</div>
                <div className="text-white font-mono text-lg">{formatExecTime(conversation.execution_time_ms)}</div>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Timestamps</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-700 p-4 rounded">
                <div className="text-gray-400 text-sm">Query Started At</div>
                <div className="text-white text-sm">{new Date(conversation.query_started_at).toLocaleString()}</div>
              </div>
              <div className="bg-gray-700 p-4 rounded">
                <div className="text-gray-400 text-sm">Latest Update</div>
                <div className="text-white text-sm">
                  {conversation.response_latest_update ? new Date(conversation.response_latest_update).toLocaleString() : 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* Technical Details */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Technical Details</h3>
            <div className="bg-gray-700 p-4 rounded">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-400">Email Hash:</div>
                <div className="text-white font-mono text-xs break-all">{conversation.email_hash}</div>
                <div className="text-gray-400">Execution Time:</div>
                <div className="text-white font-mono">{formatExecTime(conversation.execution_time_ms)}</div>
                {conversation.generated_response && (
                  <>
                    <div className="text-gray-400">Response Length:</div>
                    <div className="text-white font-mono">{conversation.generated_response.length.toLocaleString()} chars</div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Generated Response */}
          {conversation.generated_response && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Generated Response</h3>
                <button
                  onClick={() => handleCopy('response', conversation.generated_response!)}
                  className="px-3 py-1 bg-gray-600 text-gray-300 rounded text-xs hover:bg-gray-500"
                >
                  {copiedField === 'response' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="bg-gray-700 p-4 rounded max-h-96 overflow-y-auto text-left">
                <CitationRenderer
                  content={formatStoredGeneratedResponseForDisplay(conversation.generated_response)}
                  className="prose dark:prose-invert prose-neutral max-w-none prose-sm sm:prose-base"
                />
              </div>
            </div>
          )}

          {/* SQL Query / Query JSON */}
          {conversation.sql_query && (() => {
            let displayContent = conversation.sql_query;
            let lang: 'json' | 'sql' = 'sql';
            try {
              const parsed = JSON.parse(conversation.sql_query);
              displayContent = JSON.stringify(parsed, null, 2)
                .replace(/\\n/g, '\n')
                .replace(/\\t/g, '\t');
              lang = 'json';
            } catch {
              // not JSON — render as SQL
            }
            return (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white">SQL Query</h3>
                  <button
                    onClick={() => handleCopy('sql', conversation.sql_query!)}
                    className="px-3 py-1 bg-gray-600 text-gray-300 rounded text-xs hover:bg-gray-500"
                  >
                    {copiedField === 'sql' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="rounded-lg overflow-hidden border border-gray-600">
                  <SyntaxHighlighter
                    language={lang}
                    style={vscDarkPlus}
                    customStyle={{
                      margin: 0,
                      maxHeight: '480px',
                      padding: '1rem',
                      fontSize: '0.75rem',
                      borderRadius: 0,
                    }}
                    wrapLongLines={true}
                  >
                    {displayContent}
                  </SyntaxHighlighter>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// User Details Modal
// ---------------------------------------------------------------------------
const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ userDetails, onClose }) => {
  if (!userDetails) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">User Details: {userDetails.user_info.email}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">User Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-gray-700 p-4 rounded">
                <div className="text-gray-400 text-sm">Display Name</div>
                <div className="text-white font-semibold">{userDetails.user_info.display_name || 'N/A'}</div>
              </div>
              <div className="bg-gray-700 p-4 rounded">
                <div className="text-gray-400 text-sm">Last Login</div>
                <div className="text-white font-semibold">
                  {userDetails.user_info.last_login ? new Date(userDetails.user_info.last_login).toLocaleString() : 'Never'}
                </div>
              </div>
              <div className="bg-gray-700 p-4 rounded">
                <div className="text-gray-400 text-sm">Authorization Status</div>
                <div className="text-white font-semibold">
                  {userDetails.whitelist_status?.is_authorized ? 'Authorized' : 'Not Authorized'}
                </div>
              </div>
              <div className="bg-gray-700 p-4 rounded">
                <div className="text-gray-400 text-sm">Data Scope</div>
                <div className="text-white font-semibold uppercase">
                  {userDetails.user_info.user_type || userDetails.whitelist_status?.user_type || 'GLOBAL'}
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Recent Messages ({userDetails.recent_messages.length})</h3>
            <div className="bg-gray-900 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-gray-400 text-sm">Query Text</th>
                      <th className="px-4 py-2 text-left text-gray-400 text-sm">Status</th>
                      <th className="px-4 py-2 text-left text-gray-400 text-sm">Exec Time</th>
                      <th className="px-4 py-2 text-left text-gray-400 text-sm">Started At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userDetails.recent_messages.map((msg, idx) => (
                      <tr key={msg.message_id} className={idx % 2 === 0 ? 'bg-gray-800' : 'bg-gray-850'}>
                        <td className="px-4 py-2 text-white text-sm max-w-md truncate">{msg.user_query_text}</td>
                        <td className="px-4 py-2 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            msg.response_status === 'completed' ? 'bg-green-600 text-white' :
                            msg.response_status === 'failed' ? 'bg-red-600 text-white' :
                            'bg-yellow-600 text-white'
                          }`}>{msg.response_status || 'N/A'}</span>
                        </td>
                        <td className="px-4 py-2 text-white text-sm font-mono">{formatExecTime(msg.execution_time_ms)}</td>
                        <td className="px-4 py-2 text-gray-400 text-sm">{new Date(msg.query_started_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Admin Panel
// ---------------------------------------------------------------------------
const ChatAdminPanel: React.FC<ChatAdminPanelProps> = ({ adminEmail, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabId>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [whitelistEntries, setWhitelistEntries] = useState<WhitelistEntry[]>([]);
  const [conversations, setConversations] = useState<ConversationMessage[]>([]);
  const [stats, setStats] = useState<ChatAdminStats | null>(null);
  const [schemaStatus, setSchemaStatus] = useState<SchemaStatusResponse | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchEmail, setSearchEmail] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [userGroupFilter, setUserGroupFilter] = useState<UserGroupFilter>('all');
  const [conversationIdFilter, setConversationIdFilter] = useState<string>('');

  // Sorting
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'last_login', dir: 'desc' });

  // Modals
  const [selectedUserDetails, setSelectedUserDetails] = useState<UserDetails | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<ConversationMessage | null>(null);
  const [showConversationModal, setShowConversationModal] = useState(false);

  // Add user form
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserType, setNewUserType] = useState<UserType>('global');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadedTabs = useRef<Record<string, boolean>>({});

  // -------------------------------------------------------------------------
  // Per-tab lazy loaders
  // -------------------------------------------------------------------------
  const loadUsersData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await chatAdminGetAllUsers(adminEmail, { limit: 200 });
      setUsers(data || []);
      loadedTabs.current.users = true;
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [adminEmail]);

  const loadWhitelistData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await chatAdminGetAuthorizedUsers(adminEmail, { limit: 200 });
      setWhitelistEntries(data || []);
      loadedTabs.current.whitelist = true;
    } catch (err: any) {
      setError(err.message || 'Failed to load whitelist');
    } finally {
      setLoading(false);
    }
  }, [adminEmail]);

  const loadConversationsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await chatAdminGetConversations(adminEmail, { limit: 200 });
      setConversations(data || []);
      loadedTabs.current.conversations = true;
    } catch (err: any) {
      setError(err.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [adminEmail]);

  const loadStatsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await chatAdminGetStats(adminEmail);
      setStats(data);
      loadedTabs.current.stats = true;
    } catch (err: any) {
      setError(err.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [adminEmail]);

  const loadSchemaStatus = useCallback(async () => {
    setSchemaLoading(true);
    setSchemaError(null);
    try {
      const statusData = await chatApi.getSchemaStatus();
      setSchemaStatus(statusData);
      loadedTabs.current.schema = true;
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
      setSchemaError(`Failed to fetch schema status: ${msg}`);
      setSchemaStatus(null);
    } finally {
      setSchemaLoading(false);
    }
  }, []);

  // Load tab data on first activation
  useEffect(() => {
    if (!loadedTabs.current[activeTab]) {
      switch (activeTab) {
        case 'users': loadUsersData(); break;
        case 'whitelist': loadWhitelistData(); break;
        case 'conversations': loadConversationsData(); break;
        case 'stats': loadStatsData(); break;
        case 'schema': loadSchemaStatus(); break;
      }
    }
  }, [activeTab, loadUsersData, loadWhitelistData, loadConversationsData, loadStatsData, loadSchemaStatus]);

  const handleRefreshCurrentTab = () => {
    loadedTabs.current[activeTab] = false;
    switch (activeTab) {
      case 'users': loadUsersData(); break;
      case 'whitelist': loadWhitelistData(); break;
      case 'conversations': loadConversationsData(); break;
      case 'stats': loadStatsData(); break;
      case 'schema': loadSchemaStatus(); break;
    }
  };

  const invalidateWhitelistCache = () => { loadedTabs.current.whitelist = false; };
  const invalidateUsersCache = () => { loadedTabs.current.users = false; };

  // -------------------------------------------------------------------------
  // Filtering & sorting (users)
  // -------------------------------------------------------------------------
  const filteredSortedUsers = useMemo(() => {
    let list = [...users];

    // Email search
    if (searchEmail) {
      const q = searchEmail.toLowerCase();
      list = list.filter(u => u.email.toLowerCase().includes(q));
    }

    // Group filter
    switch (userGroupFilter) {
      case 'authorized':
        list = list.filter(u => u.is_authorized === true);
        break;
      case 'revoked':
        list = list.filter(u => u.is_authorized === false);
        break;
      case 'waitlisted_no_access':
        list = list.filter(u => u.is_waitlist && (u.is_authorized === null || u.is_authorized === undefined));
        break;
      case 'logged_in_no_access':
        list = list.filter(u => u.last_login && !u.is_waitlist && (u.is_authorized === null || u.is_authorized === undefined));
        break;
    }

    // Sort
    const { key, dir } = sortConfig;
    list.sort((a: any, b: any) => {
      let va = a[key], vb = b[key];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'boolean') { va = va ? 1 : 0; vb = vb ? 1 : 0; }
      if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb as string).toLowerCase(); }
      if (va < vb) return dir === 'asc' ? -1 : 1;
      if (va > vb) return dir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [users, searchEmail, userGroupFilter, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortArrow = (key: string) => sortConfig.key === key ? (sortConfig.dir === 'asc' ? ' ▲' : ' ▼') : '';

  // User group counts for quick filter badges
  const groupCounts = useMemo(() => {
    const all = users.length;
    const authorized = users.filter(u => u.is_authorized === true).length;
    const revoked = users.filter(u => u.is_authorized === false).length;
    const waitlisted = users.filter(u => u.is_waitlist && (u.is_authorized === null || u.is_authorized === undefined)).length;
    const noAccess = users.filter(u => u.last_login && !u.is_waitlist && (u.is_authorized === null || u.is_authorized === undefined)).length;
    return { all, authorized, revoked, waitlisted, noAccess };
  }, [users]);

  // -------------------------------------------------------------------------
  // Filtering (whitelist)
  // -------------------------------------------------------------------------
  const filteredWhitelistEntries = useMemo(() => {
    if (!searchEmail) return whitelistEntries;
    const q = searchEmail.toLowerCase();
    return whitelistEntries.filter(e => e.email.toLowerCase().includes(q));
  }, [whitelistEntries, searchEmail]);

  // -------------------------------------------------------------------------
  // Filtering (conversations)
  // -------------------------------------------------------------------------
  const filteredConversations = useMemo(() => {
    return conversations.filter(msg => {
      if (searchEmail && !msg.email.toLowerCase().includes(searchEmail.toLowerCase())) return false;
      if (statusFilter && msg.response_status !== statusFilter) return false;
      if (conversationIdFilter && String(msg.conversation_id) !== conversationIdFilter) return false;
      return true;
    });
  }, [conversations, searchEmail, statusFilter, conversationIdFilter]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------
  const handleViewUserDetails = async (userEmail: string) => {
    try {
      const details = await chatAdminGetUserDetails(adminEmail, userEmail);
      setSelectedUserDetails(details);
      setShowDetailsModal(true);
    } catch (err: any) {
      alert(`Failed to load user details: ${err.message}`);
    }
  };

  const handleViewConversationDetails = (conversation: ConversationMessage) => {
    setSelectedConversation(conversation);
    setShowConversationModal(true);
  };

  const handleAuthorizeUser = async (userEmail: string, userType?: UserType) => {
    if (!window.confirm(`Authorize ${userEmail}?`)) return;
    try {
      setLoading(true);
      const existingUser = users.find(u => u.email === userEmail);
      const targetType = userType || existingUser?.user_type || 'global';
      await chatAdminUpdateWhitelist(adminEmail, userEmail, true, targetType);
      invalidateUsersCache();
      invalidateWhitelistCache();
      await loadUsersData();
    } catch (err: any) {
      alert(`Failed to authorize: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeUser = async (userEmail: string) => {
    if (!window.confirm(`Revoke authorization for ${userEmail}?`)) return;
    try {
      setLoading(true);
      const existingUser = users.find(u => u.email === userEmail);
      const targetType = existingUser?.user_type || 'global';
      await chatAdminUpdateWhitelist(adminEmail, userEmail, false, targetType);
      invalidateUsersCache();
      invalidateWhitelistCache();
      await loadUsersData();
    } catch (err: any) {
      alert(`Failed to revoke: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAuthorization = async (userEmail: string, currentStatus: boolean) => {
    const action = currentStatus ? 'revoke' : 'grant';
    if (!window.confirm(`${currentStatus ? 'Revoke' : 'Grant'} authorization for ${userEmail}?`)) return;
    try {
      setLoading(true);
      const existingEntry = whitelistEntries.find(e => e.email === userEmail);
      const targetType = existingEntry?.user_type || 'global';
      await chatAdminUpdateWhitelist(adminEmail, userEmail, !currentStatus, targetType);
      invalidateUsersCache();
      invalidateWhitelistCache();
      await loadWhitelistData();
    } catch (err: any) {
      alert(`Failed to ${action} authorization: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUserTypeChange = async (userEmail: string, newType: UserType) => {
    try {
      // Optimistic update in users and whitelist state
      setUsers(prev => prev.map(u => u.email === userEmail ? { ...u, user_type: newType } : u));
      setWhitelistEntries(prev => prev.map(e => e.email === userEmail ? { ...e, user_type: newType } : e));
      await chatAdminUpdateUser(adminEmail, userEmail, { user_type: newType });
    } catch (err: any) {
      alert(`Failed to update user type: ${err.message}`);
      loadUsersData();
      loadWhitelistData();
    }
  };

  const handleWhitelistUserTypeChange = async (userEmail: string, currentAuthorized: boolean, newType: UserType) => {
    try {
      // Optimistic update
      setWhitelistEntries(prev => prev.map(e => e.email === userEmail ? { ...e, user_type: newType } : e));
      setUsers(prev => prev.map(u => u.email === userEmail ? { ...u, user_type: newType } : u));
      await chatAdminUpdateWhitelist(adminEmail, userEmail, currentAuthorized, newType);
    } catch (err: any) {
      alert(`Failed to update whitelist user type: ${err.message}`);
      loadWhitelistData();
      loadUsersData();
    }
  };

  const handleRemoveFromWhitelist = async (userEmail: string) => {
    if (!window.confirm(`Remove ${userEmail} from whitelist entirely?`)) return;
    try {
      setLoading(true);
      await chatAdminRemoveWhitelist(adminEmail, userEmail);
      invalidateUsersCache();
      invalidateWhitelistCache();
      await loadWhitelistData();
    } catch (err: any) {
      alert(`Failed to remove: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUserToWhitelist = async () => {
    if (!newUserEmail.trim()) return;
    try {
      setLoading(true);
      await chatAdminUpdateWhitelist(adminEmail, newUserEmail.trim(), true, newUserType);
      setNewUserEmail('');
      setNewUserType('global');
      setShowAddUserForm(false);
      invalidateUsersCache();
      invalidateWhitelistCache();
      await loadUsersData();
    } catch (err: any) {
      alert(`Failed to add user: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      const data = await chatAdminExportData(adminEmail);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-admin-export-${new Date().toISOString()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(`Failed to export data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImportData = () => { fileInputRef.current?.click(); };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) { alert('Please select a JSON file'); return; }
    try {
      setLoading(true);
      const text = await file.text();
      const data = JSON.parse(text);
      if (!window.confirm(`Import data from ${file.name}? This will APPEND to the database.`)) return;
      await chatAdminImportData(adminEmail, data);
      loadedTabs.current = {};
      handleRefreshCurrentTab();
    } catch (err: any) {
      alert(`Failed to import data: ${err.message}`);
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRefreshSchemaCache = async () => {
    if (!window.confirm('Trigger a schema cache refresh? This will re-sample all tables.')) return;
    try {
      setRefreshing(true);
      setSchemaError(null);
      await chatApi.refreshSchemaCache(adminEmail);
      loadedTabs.current.schema = false;
      await loadSchemaStatus();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
      setSchemaError(`Refresh failed: ${msg}`);
    } finally {
      setRefreshing(false);
    }
  };

  // -------------------------------------------------------------------------
  // Quick filter button helper
  // -------------------------------------------------------------------------
  const FilterBtn: React.FC<{ id: UserGroupFilter; label: string; count: number }> = ({ id, label, count }) => (
    <button
      onClick={() => setUserGroupFilter(id)}
      className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap transition-colors ${
        userGroupFilter === id
          ? 'bg-blue-600 text-white'
          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
      }`}
    >
      {label} <span className="ml-1 opacity-70">({count})</span>
    </button>
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6 z-50 overflow-y-auto">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Chat Admin Panel</h1>
            <p className="text-gray-400">Monitor user database logs and activity</p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleExportData} disabled={loading} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50" title="Export all data as JSON">Export</button>
            <button onClick={handleImportData} disabled={loading} className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50" title="Import data from JSON">Import</button>
            <button onClick={handleRefreshCurrentTab} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            <button onClick={onClose} className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600">Close</button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex gap-2 bg-gray-800 p-2 rounded-lg">
          {([
            ['users', 'Users'],
            ['whitelist', 'Whitelist'],
            ['conversations', 'Conversations'],
            ['stats', 'Statistics'],
            ['schema', 'Schema Cache'],
          ] as [TabId, string][]).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 py-2 px-4 rounded transition-colors ${
                activeTab === id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {error && <div className="mb-4 p-4 bg-red-900 text-white rounded-lg">{error}</div>}

        {loading && <div className="text-center py-8"><div className="text-white text-xl">Loading...</div></div>}

        {/* ================================================================= */}
        {/* USERS TAB                                                         */}
        {/* ================================================================= */}
        {!loading && activeTab === 'users' && (
          <div className="bg-gray-800 rounded-lg p-6">
            {/* Quick group filters */}
            <div className="mb-4 flex flex-wrap gap-2">
              <FilterBtn id="all" label="All Users" count={groupCounts.all} />
              <FilterBtn id="authorized" label="Authorized" count={groupCounts.authorized} />
              {groupCounts.waitlisted > 0 && (
                <FilterBtn id="waitlisted_no_access" label="Waitlisted (needs review)" count={groupCounts.waitlisted} />
              )}
              {groupCounts.noAccess > 0 && (
                <FilterBtn id="logged_in_no_access" label="Logged in, no access" count={groupCounts.noAccess} />
              )}
              {groupCounts.revoked > 0 && (
                <FilterBtn id="revoked" label="Revoked" count={groupCounts.revoked} />
              )}
            </div>

            {/* Search + Add user */}
            <div className="mb-4 flex gap-4 items-center">
              <input
                type="text"
                placeholder="Search by email..."
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                className="px-4 py-2 bg-gray-700 text-white rounded flex-1 md:flex-initial md:w-96"
              />
              <button
                onClick={() => setShowAddUserForm(!showAddUserForm)}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
              >
                {showAddUserForm ? 'Cancel' : '+ Add User'}
              </button>
            </div>

            {/* Add User Form */}
            {showAddUserForm && (
              <div className="mb-6 bg-gray-700 p-4 rounded-lg">
                <h3 className="text-white font-semibold mb-3">Add User to Whitelist</h3>
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="block text-gray-300 text-sm mb-1">Email Address</label>
                    <input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      placeholder="user@example.com"
                      className="w-full px-3 py-2 bg-gray-600 text-white rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddUserToWhitelist()}
                    />
                  </div>
                  <div className="w-36">
                    <label className="block text-gray-300 text-sm mb-1">Data Scope</label>
                    <select
                      value={newUserType}
                      onChange={(e) => setNewUserType(e.target.value as UserType)}
                      className="w-full px-3 py-2 bg-gray-600 text-white rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="global">Global</option>
                      <option value="eme">EME</option>
                      <option value="nam">NAM</option>
                    </select>
                  </div>
                  <button
                    onClick={handleAddUserToWhitelist}
                    disabled={!newUserEmail.trim()}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    Add &amp; Authorize
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-400">Creates user in cc_ai.users with selected data slice, then adds to whitelist with is_authorized=TRUE</p>
              </div>
            )}

            {/* Users Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left text-white cursor-pointer select-none hover:text-blue-300" onClick={() => handleSort('email')}>
                      Email{sortArrow('email')}
                    </th>
                    <th className="px-3 py-2 text-left text-white cursor-pointer select-none hover:text-blue-300" onClick={() => handleSort('display_name')}>
                      Name{sortArrow('display_name')}
                    </th>
                    <th className="px-3 py-2 text-left text-white cursor-pointer select-none hover:text-blue-300" onClick={() => handleSort('last_login')}>
                      Last Login{sortArrow('last_login')}
                    </th>
                    <th className="px-2 py-2 text-left text-white cursor-pointer select-none hover:text-blue-300" onClick={() => handleSort('is_waitlist')}>
                      WL{sortArrow('is_waitlist')}
                    </th>
                    <th className="px-2 py-2 text-left text-white cursor-pointer select-none hover:text-blue-300" onClick={() => handleSort('is_authorized')}>
                      Auth{sortArrow('is_authorized')}
                    </th>
                    <th className="px-2 py-2 text-left text-white cursor-pointer select-none hover:text-blue-300" onClick={() => handleSort('user_type')}>
                      Scope{sortArrow('user_type')}
                    </th>
                    <th className="px-2 py-2 text-left text-white">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSortedUsers.map((user, idx) => (
                    <tr key={user.email} className={idx % 2 === 0 ? 'bg-gray-750' : 'bg-gray-800'}>
                      <td className="px-3 py-1.5 text-white">{user.email}</td>
                      <td className="px-3 py-1.5 text-gray-300">{user.display_name || 'N/A'}</td>
                      <td className="px-3 py-1.5 text-gray-300 whitespace-nowrap">
                        {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-2 py-1.5">
                        {user.is_waitlist ? (
                          <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">Yes</span>
                        ) : (
                          <span className="text-gray-500 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        {user.is_authorized === true ? (
                          <span className="px-1.5 py-0.5 bg-green-600/20 text-green-400 rounded text-xs">Yes</span>
                        ) : user.is_authorized === false ? (
                          <span className="px-1.5 py-0.5 bg-red-600/20 text-red-400 rounded text-xs">Revoked</span>
                        ) : (
                          <span className="px-1.5 py-0.5 bg-gray-600/30 text-gray-400 rounded text-xs">No</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <select
                          value={user.user_type || 'global'}
                          onChange={(e) => handleUserTypeChange(user.email, e.target.value as UserType)}
                          className="px-2 py-1 bg-gray-700 text-white border border-gray-600 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer font-medium uppercase"
                        >
                          <option value="global">GLOBAL</option>
                          <option value="eme">EME</option>
                          <option value="nam">NAM</option>
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleViewUserDetails(user.email)}
                            className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                          >
                            Details
                          </button>
                          {user.is_authorized === true ? (
                            <button
                              onClick={() => handleRevokeUser(user.email)}
                              className="px-2 py-1 bg-yellow-600 text-white rounded text-xs hover:bg-yellow-700"
                            >
                              Revoke
                            </button>
                          ) : (
                            <button
                              onClick={() => handleAuthorizeUser(user.email)}
                              className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                            >
                              Authorize
                            </button>
                          )}
                          {(user.is_authorized === true || user.is_authorized === false) && (
                            <button
                              onClick={() => handleRemoveFromWhitelist(user.email)}
                              className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                              title="Remove from whitelist entirely"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredSortedUsers.length === 0 && (
                <div className="text-center py-8 text-gray-400">No users match the current filters</div>
              )}
            </div>
            <div className="mt-3 text-xs text-gray-500">Showing {filteredSortedUsers.length} of {users.length} users</div>
          </div>
        )}

        {/* ================================================================= */}
        {/* WHITELIST TAB                                                     */}
        {/* ================================================================= */}
        {!loading && activeTab === 'whitelist' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="mb-4 flex gap-4 items-center">
              <input
                type="text"
                placeholder="Search by email..."
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                className="px-4 py-2 bg-gray-700 text-white rounded flex-1 md:flex-initial md:w-96"
              />
              <button
                onClick={() => setShowAddUserForm(!showAddUserForm)}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                {showAddUserForm ? 'Cancel' : '+ Add User'}
              </button>
            </div>

            {showAddUserForm && (
              <div className="mb-6 bg-gray-700 p-4 rounded-lg">
                <h3 className="text-white font-semibold mb-3">Add User to Whitelist</h3>
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="block text-gray-300 text-sm mb-1">Email Address</label>
                    <input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      placeholder="user@example.com"
                      className="w-full px-3 py-2 bg-gray-600 text-white rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddUserToWhitelist()}
                    />
                  </div>
                  <div className="w-36">
                    <label className="block text-gray-300 text-sm mb-1">Data Scope</label>
                    <select
                      value={newUserType}
                      onChange={(e) => setNewUserType(e.target.value as UserType)}
                      className="w-full px-3 py-2 bg-gray-600 text-white rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="global">Global</option>
                      <option value="eme">EME</option>
                      <option value="nam">NAM</option>
                    </select>
                  </div>
                  <button
                    onClick={handleAddUserToWhitelist}
                    disabled={!newUserEmail.trim()}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    Add &amp; Authorize
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-400">Creates user in cc_ai.users with selected data slice, then adds to whitelist with is_authorized=TRUE</p>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-white">Email</th>
                    <th className="px-4 py-2 text-left text-white">Authorized</th>
                    <th className="px-4 py-2 text-left text-white">Data Scope</th>
                    <th className="px-4 py-2 text-left text-white">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWhitelistEntries.map((entry, idx) => (
                    <tr key={entry.email} className={idx % 2 === 0 ? 'bg-gray-750' : 'bg-gray-800'}>
                      <td className="px-4 py-2 text-white">{entry.email}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          entry.is_authorized ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                        }`}>
                          {entry.is_authorized ? 'Authorized' : 'Revoked'}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <select
                          value={entry.user_type || 'global'}
                          onChange={(e) => handleWhitelistUserTypeChange(entry.email, entry.is_authorized, e.target.value as UserType)}
                          className="px-2 py-1 bg-gray-700 text-white border border-gray-600 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer font-medium uppercase"
                        >
                          <option value="global">GLOBAL</option>
                          <option value="eme">EME</option>
                          <option value="nam">NAM</option>
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleToggleAuthorization(entry.email, entry.is_authorized)}
                            className={`px-3 py-1 text-white rounded text-sm ${
                              entry.is_authorized
                                ? 'bg-yellow-600 hover:bg-yellow-700'
                                : 'bg-green-600 hover:bg-green-700'
                            }`}
                          >
                            {entry.is_authorized ? 'Revoke' : 'Grant'}
                          </button>
                          <button
                            onClick={() => handleRemoveFromWhitelist(entry.email)}
                            className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredWhitelistEntries.length === 0 && (
                <div className="text-center py-8 text-gray-400">No whitelist entries found</div>
              )}
            </div>
          </div>
        )}

        {/* ================================================================= */}
        {/* CONVERSATIONS TAB                                                 */}
        {/* ================================================================= */}
        {!loading && activeTab === 'conversations' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="mb-4 flex flex-wrap gap-4">
              <input
                type="text"
                placeholder="Filter by email..."
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                className="px-4 py-2 bg-gray-700 text-white rounded flex-1 md:flex-initial md:w-72"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-gray-700 text-white rounded"
              >
                <option value="">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
              </select>
              <input
                type="text"
                placeholder="Filter by Conversation ID..."
                value={conversationIdFilter}
                onChange={(e) => setConversationIdFilter(e.target.value)}
                className="px-4 py-2 bg-gray-700 text-white rounded md:w-56 font-mono text-sm"
              />
              {conversationIdFilter && (
                <button onClick={() => setConversationIdFilter('')} className="px-3 py-2 bg-gray-600 text-gray-300 rounded text-sm hover:bg-gray-500">Clear</button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left text-white">Msg ID</th>
                    <th className="px-3 py-2 text-left text-white">Conv ID</th>
                    <th className="px-3 py-2 text-left text-white">User Email</th>
                    <th className="px-3 py-2 text-left text-white">Query Text</th>
                    <th className="px-3 py-2 text-left text-white">Status</th>
                    <th className="px-3 py-2 text-left text-white">Citation</th>
                    <th className="px-3 py-2 text-center text-white" title="Has generated response">Resp</th>
                    <th className="px-3 py-2 text-center text-white" title="Has SQL query">SQL</th>
                    <th className="px-3 py-2 text-left text-white">Exec Time</th>
                    <th className="px-3 py-2 text-left text-white">Started At</th>
                    <th className="px-3 py-2 text-left text-white">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConversations.map((msg, idx) => (
                    <tr key={msg.message_id} className={idx % 2 === 0 ? 'bg-gray-750' : 'bg-gray-800'}>
                      <td className="px-3 py-2 text-gray-300 font-mono text-xs">{msg.message_id}</td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => setConversationIdFilter(String(msg.conversation_id))}
                          className="text-blue-400 font-mono text-xs hover:underline"
                          title="Filter by this conversation"
                        >
                          {msg.conversation_id}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-white">{msg.email}</td>
                      <td className="px-3 py-2 text-gray-300 max-w-xs">
                        <div className="truncate" title={msg.user_query_text}>{msg.user_query_text}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
                          msg.response_status === 'completed' ? 'bg-green-600 text-white' :
                          msg.response_status === 'failed' ? 'bg-red-600 text-white' :
                          msg.response_status === 'in_progress' ? 'bg-blue-600 text-white' :
                          'bg-gray-600 text-white'
                        }`}>{msg.response_status || 'unknown'}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
                          msg.citation_status === 'completed' || msg.citation_status === 'generated' ? 'bg-green-600 text-white' :
                          msg.citation_status === 'failed' ? 'bg-red-600 text-white' :
                          msg.citation_status === 'in_progress' ? 'bg-blue-600 text-white' :
                          'bg-gray-600 text-white'
                        }`}>{msg.citation_status || 'N/A'}</span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {msg.generated_response ? (
                          <span className="text-green-400 text-xs" title={`${msg.generated_response.length} chars`}>&#10003;</span>
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {msg.sql_query ? (
                          <span className="text-green-400 text-xs">&#10003;</span>
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-300 font-mono text-xs">
                        {msg.execution_time_ms ? formatExecTime(msg.execution_time_ms) : 'N/A'}
                      </td>
                      <td className="px-3 py-2 text-gray-400 text-xs">
                        {new Date(msg.query_started_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => handleViewConversationDetails(msg)}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredConversations.length === 0 && (
                <div className="text-center py-8 text-gray-400">No conversations found</div>
              )}
            </div>

            <div className="mt-4 text-xs text-gray-400">
              Showing {filteredConversations.length} of {conversations.length} conversation(s)
              {conversationIdFilter && <span className="ml-2 text-blue-400">| Filtered to conversation #{conversationIdFilter}</span>}
            </div>
          </div>
        )}

        {/* ================================================================= */}
        {/* STATS TAB                                                         */}
        {/* ================================================================= */}
        {!loading && activeTab === 'stats' && stats && (
          <div className="space-y-6">
            {/* Overview Cards - Row 1 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-800 p-6 rounded-lg">
                <div className="text-gray-400 text-sm mb-1">Total Users</div>
                <div className="text-3xl font-bold text-white">{stats.total_users}</div>
              </div>
              <div className="bg-gray-800 p-6 rounded-lg">
                <div className="text-gray-400 text-sm mb-1">Authorized Users</div>
                <div className="text-3xl font-bold text-white">{stats.total_authorized_users}</div>
              </div>
              <div className="bg-gray-800 p-6 rounded-lg">
                <div className="text-gray-400 text-sm mb-1">Total Conversations</div>
                <div className="text-3xl font-bold text-white">{stats.total_conversations}</div>
              </div>
              <div className="bg-gray-800 p-6 rounded-lg">
                <div className="text-gray-400 text-sm mb-1">Avg Exec Time</div>
                <div className="text-3xl font-bold text-white">{formatExecTime(stats.avg_execution_time_ms)}</div>
              </div>
            </div>

            {/* Overview Cards - Row 2 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-800 p-6 rounded-lg">
                <div className="text-gray-400 text-sm mb-1">Waitlisted Users</div>
                <div className="text-3xl font-bold text-amber-400">{stats.waitlisted_users}</div>
              </div>
              <div className="bg-gray-800 p-6 rounded-lg">
                <div className="text-gray-400 text-sm mb-1">Pending Access</div>
                <div className="text-3xl font-bold text-orange-400">{stats.users_pending_access}</div>
              </div>
              <div className="bg-gray-800 p-6 rounded-lg">
                <div className="text-gray-400 text-sm mb-1">With Generated Response</div>
                <div className="text-3xl font-bold text-green-400">{stats.conversations_with_response}</div>
              </div>
              <div className="bg-gray-800 p-6 rounded-lg">
                <div className="text-gray-400 text-sm mb-1">Success Rate</div>
                <div className="text-3xl font-bold text-white">
                  {stats.total_conversations > 0
                    ? `${Math.round(((stats.conversations_by_status?.completed || 0) / stats.total_conversations) * 100)}%`
                    : 'N/A'}
                </div>
              </div>
            </div>

            {/* Conversations by Status + Citation side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-800 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-white mb-4">Conversations by Status</h3>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(stats.conversations_by_status).map(([status, count]) => (
                    <div key={status} className="bg-gray-700 p-4 rounded">
                      <div className="text-gray-400 text-sm capitalize">{status}</div>
                      <div className="text-2xl font-bold text-white">{count as number}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gray-800 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-white mb-4">Conversations by Citation</h3>
                <div className="grid grid-cols-2 gap-4">
                  {stats.conversations_by_citation && Object.entries(stats.conversations_by_citation).map(([status, count]) => (
                    <div key={status} className="bg-gray-700 p-4 rounded">
                      <div className="text-gray-400 text-sm capitalize">{status}</div>
                      <div className="text-2xl font-bold text-white">{count as number}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Conversations Over Time (last 30 days) */}
            {stats.conversations_per_day && stats.conversations_per_day.length > 0 && (
              <div className="bg-gray-800 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-white mb-4">Conversations Over Time (Last 30 Days)</h3>
                <div className="flex items-end gap-1 h-40">
                  {(() => {
                    const maxCount = Math.max(...stats.conversations_per_day.map(d => d.count), 1);
                    return stats.conversations_per_day.map((d) => (
                      <div key={d.day} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                        <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                          {d.day}: {d.count}
                        </div>
                        <div
                          className="w-full bg-blue-500 rounded-t min-h-[2px] transition-all hover:bg-blue-400"
                          style={{ height: `${(d.count / maxCount) * 100}%` }}
                        />
                      </div>
                    ));
                  })()}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>{stats.conversations_per_day[0]?.day}</span>
                  <span>{stats.conversations_per_day[stats.conversations_per_day.length - 1]?.day}</span>
                </div>
              </div>
            )}

            {/* Most Active Users (by query count) */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-white mb-4">Most Active Users (by Query Count)</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-4 py-2 text-left text-white">#</th>
                      <th className="px-4 py-2 text-left text-white">Email</th>
                      <th className="px-4 py-2 text-left text-white">Display Name</th>
                      <th className="px-4 py-2 text-left text-white">Queries</th>
                      <th className="px-4 py-2 text-left text-white">Last Login</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.most_active_users.map((user, idx) => (
                      <tr key={user.email} className={idx % 2 === 0 ? 'bg-gray-750' : 'bg-gray-800'}>
                        <td className="px-4 py-2 text-gray-500 text-sm">{idx + 1}</td>
                        <td className="px-4 py-2 text-white">{user.email}</td>
                        <td className="px-4 py-2 text-gray-300">{user.display_name || 'N/A'}</td>
                        <td className="px-4 py-2 text-white font-semibold">{user.query_count}</td>
                        <td className="px-4 py-2 text-gray-400">
                          {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Extra info */}
            {stats.avg_response_length !== undefined && stats.avg_response_length > 0 && (
              <div className="bg-gray-800 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-white mb-2">Response Metrics</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-gray-700 p-4 rounded">
                    <div className="text-gray-400 text-sm">Avg Response Length</div>
                    <div className="text-white text-xl font-bold">{Math.round(stats.avg_response_length).toLocaleString()} chars</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded">
                    <div className="text-gray-400 text-sm">With SQL Query</div>
                    <div className="text-white text-xl font-bold">{stats.conversations_with_response}</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded">
                    <div className="text-gray-400 text-sm">Without Response</div>
                    <div className="text-white text-xl font-bold">{stats.conversations_without_response}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ================================================================= */}
        {/* SCHEMA CACHE TAB                                                  */}
        {/* ================================================================= */}
        {!loading && activeTab === 'schema' && (
          <div>
            <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4 mb-6">
              <h3 className="text-blue-400 font-bold mb-2">Schema Sample Cache</h3>
              <p className="text-gray-300 text-sm mb-2">
                Schema samples help the AI understand table data diversity. The cache refreshes automatically every 21 days.
              </p>
              <ul className="text-gray-400 text-xs space-y-1 list-disc list-inside">
                <li>Samples use diversity-maximizing selection to show varied categorical values</li>
                <li>Each environment (dev/prod) maintains its own independent cache</li>
                <li>Use the button below to trigger a manual refresh when data changes significantly</li>
              </ul>
            </div>

            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              <h3 className="text-white font-bold text-lg mb-4">Cache Status</h3>
              {schemaLoading ? (
                <div className="flex items-center gap-3 text-gray-400">
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-400" />
                  Fetching status...
                </div>
              ) : schemaError ? (
                <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
                  <p className="text-red-400 font-medium">Error</p>
                  <p className="text-gray-300 text-sm mt-1">{schemaError}</p>
                </div>
              ) : schemaStatus === null ? (
                <p className="text-gray-500 text-sm">Navigate to this tab to load status.</p>
              ) : !schemaStatus.cached ? (
                <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
                  <p className="text-red-400 font-medium">No cache found</p>
                  <p className="text-gray-400 text-sm mt-1">
                    {schemaStatus.message || 'Use the refresh button below to generate the initial cache.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      schemaStatus.is_fresh
                        ? 'bg-green-900/40 text-green-400 border border-green-700'
                        : 'bg-amber-900/40 text-amber-400 border border-amber-700'
                    }`}>{schemaStatus.is_fresh ? 'Fresh' : 'Stale'}</span>
                    {schemaStatus.age_days !== undefined && (
                      <span className="text-gray-400 text-sm">
                        Generated {schemaStatus.age_days.toFixed(1)} day{schemaStatus.age_days !== 1 ? 's' : ''} ago
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-gray-700 p-4 rounded">
                      <div className="text-gray-400 text-sm">Generated At</div>
                      <div className="text-white text-sm font-medium">
                        {schemaStatus.generated_at ? new Date(schemaStatus.generated_at).toLocaleString() : '—'}
                      </div>
                    </div>
                    <div className="bg-gray-700 p-4 rounded">
                      <div className="text-gray-400 text-sm">Expires At</div>
                      <div className="text-white text-sm font-medium">
                        {schemaStatus.expires_at ? new Date(schemaStatus.expires_at).toLocaleString() : '—'}
                      </div>
                    </div>
                    <div className="bg-gray-700 p-4 rounded">
                      <div className="text-gray-400 text-sm">TTL</div>
                      <div className="text-white text-sm font-medium">{schemaStatus.ttl_days ?? '—'} days</div>
                    </div>
                    <div className="bg-gray-700 p-4 rounded">
                      <div className="text-gray-400 text-sm">Rows per Table</div>
                      <div className="text-white text-sm font-medium">{schemaStatus.sample_rows_per_table ?? '—'}</div>
                    </div>
                    <div className="bg-gray-700 p-4 rounded">
                      <div className="text-gray-400 text-sm">Sampling Method</div>
                      <div className="text-white text-sm font-medium capitalize">
                        {schemaStatus.sampling_method?.replace(/_/g, ' ') ?? '—'}
                      </div>
                    </div>
                    <div className="bg-gray-700 p-4 rounded">
                      <div className="text-gray-400 text-sm">Tables Covered</div>
                      <div className="text-white text-sm font-medium">{schemaStatus.tables?.length ?? '—'}</div>
                    </div>
                  </div>

                  {schemaStatus.tables && schemaStatus.tables.length > 0 && (
                    <div>
                      <div className="text-gray-400 text-sm mb-2">Cached Tables</div>
                      <div className="flex flex-wrap gap-2">
                        {schemaStatus.tables.map((t) => (
                          <span key={t} className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded font-mono">{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {schemaStatus?.schema_samples && Object.keys(schemaStatus.schema_samples).length > 0 && (
              <div className="mb-6 space-y-4">
                <h3 className="text-white font-bold text-lg">Sample Data</h3>
                {Object.entries(schemaStatus.schema_samples).map(([tableName, columns]) => {
                  const colNames = Object.keys(columns);
                  const rowCount = colNames.length > 0 ? columns[colNames[0]].length : 0;
                  return (
                    <details key={tableName} className="bg-gray-800 rounded-lg overflow-hidden" open>
                      <summary className="px-4 py-3 cursor-pointer text-white font-mono text-sm font-semibold hover:bg-gray-700 flex items-center gap-2 select-none">
                        <span className="text-gray-400 text-xs font-normal">{rowCount} rows &middot; {colNames.length} cols</span>
                        <span className="ml-auto text-xs text-gray-500">click to collapse</span>
                        {tableName}
                      </summary>
                      <div className="overflow-x-auto border-t border-gray-700">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-700 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left text-gray-400 font-medium whitespace-nowrap">#</th>
                              {colNames.map((col) => (
                                <th key={col} className="px-3 py-2 text-left text-gray-300 font-mono font-medium whitespace-nowrap">{col}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from({ length: rowCount }, (_, i) => (
                              <tr key={i} className={i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'}>
                                <td className="px-3 py-2 text-gray-500 font-mono">{i + 1}</td>
                                {colNames.map((col) => {
                                  const val = columns[col][i];
                                  return (
                                    <td key={col} className="px-3 py-2 text-gray-300 whitespace-nowrap font-mono">
                                      {val === null || val === undefined
                                        ? <span className="text-gray-600 italic">null</span>
                                        : String(val)}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  );
                })}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleRefreshSchemaCache}
                disabled={refreshing}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {refreshing ? (
                  <>
                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" />
                    Refreshing...
                  </>
                ) : (
                  'Refresh Cache Now'
                )}
              </button>
              <button
                onClick={() => { loadedTabs.current.schema = false; loadSchemaStatus(); }}
                disabled={schemaLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Reload Status
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input for import */}
      <input ref={fileInputRef} type="file" accept=".json" onChange={handleFileSelect} style={{ display: 'none' }} />

      {/* Modals */}
      {showDetailsModal && (
        <UserDetailsModal
          userDetails={selectedUserDetails}
          onClose={() => { setShowDetailsModal(false); setSelectedUserDetails(null); }}
        />
      )}
      {showConversationModal && (
        <ConversationDetailsModal
          conversation={selectedConversation}
          onClose={() => { setShowConversationModal(false); setSelectedConversation(null); }}
        />
      )}
    </div>
  );
};

export default ChatAdminPanel;
